// src/pages/settings/reportTemplates/ReportPage.tsx

import React, { useState, useEffect } from "react";
import { Button, Tooltip, Typography } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import PageContainer from "../../../../components/shared/PageContainer";
import PageToolbar from "../../../../components/shared/PageToolbar";
import { DataTable } from "../../../../components/shared";

import { fetchReportTemplates } from "../../../../api/healthcare/ReportApi";
import { ReportTemplate, defaultTemplate } from "../../../../types/healthcare/ReportTypes";
import ReportBuilderDrawer from "./ReportBuilderDrawer";

const { Text } = Typography;

export const ReportPage: React.FC<{ embedded?: boolean }> = () => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [templates, setTemplates] = useState<ReportTemplate[]>([]);
	const [template, setTemplate] = useState<ReportTemplate | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");

	useEffect(() => {
		loadTemplates();
	}, []);

	const loadTemplates = async () => {
		setIsLoading(true);
		try {
			const { data } = await fetchReportTemplates();
			if (data) setTemplates(data);
		} finally {
			setIsLoading(false);
		}
	};

	const handleReset = () => {
		setSearch("");
	};

	const handleCreateTemplate = () => {
		setTemplate({ ...defaultTemplate, name: "", body: "", description: "" });
		setIsDrawerOpen(true);
	};

	const onEditTemplate = (tpl: ReportTemplate) => {
		setTemplate(tpl);
		setIsDrawerOpen(true);
	};

	const onDrawerClose = () => {
		setTemplate(null);
		setIsDrawerOpen(false);
		loadTemplates();
	};

	const filtered = templates.filter(
		(t) => !search || t.name.toLowerCase().includes(search.toLowerCase()),
	);

	const columns: ColumnsType<ReportTemplate> = [
		{
			title: "Name",
			dataIndex: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (name) => <Text strong>{name}</Text>,
		},
		{
			title: "Description",
			dataIndex: "description",
			ellipsis: true,
			sorter: (a, b) => (a.description || "").localeCompare(b.description || ""),
			render: (desc) =>
				desc ? (
					<Text type="secondary">{desc}</Text>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: "Created",
			dataIndex: "created_at",
			width: 140,
			sorter: (a, b) =>
				new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
			render: (date) => (
				<Text type="secondary">{dayjs(date).format("D MMM YYYY")}</Text>
			),
			defaultSortOrder: "descend",
		},
		{
			title: "",
			key: "actions",
			width: 60,
			render: (_, record) => (
				<Tooltip title="Edit">
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							onEditTemplate(record);
						}}
					/>
				</Tooltip>
			),
		},
	];

	return (
		<PageContainer>
			<PageToolbar
				primaryAction={{
					label: "New Template",
					icon: <PlusOutlined />,
					onClick: handleCreateTemplate,
				}}
				filters={[
					{
						type: "search",
						placeholder: "Search templates…",
						value: search,
						onChange: setSearch,
						width: 260,
					},
				]}
				onReset={handleReset}
			/>
			<DataTable
				columns={columns}
				dataSource={filtered}
				rowKey="id"
				loading={isLoading}
				onRowClick={onEditTemplate}
				emptyTitle="No report templates yet"
				emptyDescription="Create your first report template to get started"
				emptyAction={{ label: "New Template", onClick: handleCreateTemplate }}
			/>
			{isDrawerOpen && template && (
				<ReportBuilderDrawer
					template={template}
					isOpen={isDrawerOpen}
					isSubmitting={isLoading}
					setIsSubmitting={setIsLoading}
					onClose={onDrawerClose}
				/>
			)}
		</PageContainer>
	);

};

export default ReportPage;

